import { filterClasses } from "@swivl-mono/helpers";
import { motion } from "motion/react";
import { ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronLeft } from "react-icons/fi";
import { IoClose } from "react-icons/io5";

import { useActionSheetStackContext } from "./ActionSheetStack";

export enum ActionSheetAnimationDirection {
  BOTTOM = "bottom",
  RIGHT = "right"
}

export interface ActionSheetProps {
  children: ReactNode;
  className?: string;
  animationDirection?: ActionSheetAnimationDirection;
  footer?: ReactNode;
  id?: string;
  isOpen: boolean;
  maxHeight?: string;
  onBack?: () => void;
  onClose: () => void;
  swipeToDismiss?: boolean;
  title: string;
}

const SWIPE_THRESHOLD = 100;

const closedPosition = {
  [ActionSheetAnimationDirection.BOTTOM]: { x: 0, y: "100%" },
  [ActionSheetAnimationDirection.RIGHT]: { x: "100%", y: 0 }
};

const ActionSheet = ({
  children,
  className,
  animationDirection = ActionSheetAnimationDirection.BOTTOM,
  footer,
  id: externalId,
  isOpen,
  maxHeight = "90vh",
  onBack,
  onClose,
  swipeToDismiss = false,
  title
}: ActionSheetProps): ReactNode => {
  const internalId = useId();
  const sheetId = externalId || internalId;
  const stack = useActionSheetStackContext();
  const isInStack = stack !== null;
  const isFirst = stack?.isFirst(sheetId) ?? true;
  const isTop = stack?.isTop(sheetId) ?? true;

  // Keep portal mounted during exit animation
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    }
  }, [isOpen]);

  const handleAnimationComplete = useCallback(() => {
    if (!isOpen) {
      setMounted(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isInStack) return;

    if (isOpen) {
      stack.push(sheetId);
    } else {
      stack.remove(sheetId);
    }

    return () => {
      stack.remove(sheetId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number; y: number } }) => {
      if (!swipeToDismiss || animationDirection !== ActionSheetAnimationDirection.BOTTOM) return;
      if (info.offset.y > SWIPE_THRESHOLD) {
        onClose();
      }
    },
    [swipeToDismiss, animationDirection, onClose]
  );

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const contentRef = useRef<HTMLDivElement>(null);
  const [headerShadow, setHeaderShadow] = useState(false);
  const [footerShadow, setFooterShadow] = useState(false);

  const updateShadows = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setHeaderShadow(el.scrollTop > 0);
    setFooterShadow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || !isOpen) return;

    updateShadows();
    el.addEventListener("scroll", updateShadows, { passive: true });

    const observer = new ResizeObserver(updateShadows);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", updateShadows);
      observer.disconnect();
    };
  }, [isOpen, updateShadows]);

  const isBottom = animationDirection === ActionSheetAnimationDirection.BOTTOM;
  const isClosingAll = stack?.isClosingAll ?? false;
  const closed = isClosingAll ? closedPosition[ActionSheetAnimationDirection.BOTTOM] : closedPosition[animationDirection];
  const sheetTarget = isOpen ? (isTop ? { x: 0, y: 0 } : { x: "-100%", y: 0 }) : closed;

  if (!mounted && !isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop — only render for the bottom-most sheet in a stack, or when standalone */}
      {(!isInStack || isFirst) && (
        <motion.div
          className="fixed inset-0 z-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
          style={{ pointerEvents: isOpen ? "auto" : "none" }}
        >
          <div className="size-full bg-black/20" />
        </motion.div>
      )}

      {/* Sheet */}
      <motion.div
        className={filterClasses(
          "fixed z-500 flex flex-col overflow-hidden bg-white shadow-lg",
          "right-0 bottom-0 left-0 rounded-tl-xl rounded-tr-xl",
          !isTop && "pointer-events-none",
          className
        )}
        style={{
          maxHeight
        }}
        initial={closed}
        animate={sheetTarget}
        onAnimationComplete={handleAnimationComplete}
        transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
        drag={swipeToDismiss && isBottom ? "y" : false}
        dragConstraints={{ top: 0 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div className={filterClasses("relative z-10 flex shrink-0 items-center justify-between p-4", headerShadow && "shadow-panel")}>
          <div className="flex items-center gap-1">
            {onBack && (
              <button className="flex cursor-pointer items-center justify-center" onClick={onBack} aria-label="Back">
                <FiChevronLeft className="size-4 text-black" />
              </button>
            )}
            <p className="heading-base-alt text-black">{title}</p>
          </div>
          <button
            className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-grey-100"
            onClick={isInStack ? stack.closeAll : onClose}
            aria-label="Close"
          >
            <IoClose className="size-4 text-black" />
          </button>
        </div>

        {/* Content */}
        <div ref={contentRef} className="grid flex-1 overflow-y-auto overscroll-none px-4">
          {children}
        </div>

        {/* Footer */}
        {footer && <div className={filterClasses("relative z-10 shrink-0 p-4", footerShadow && "shadow-panel-top")}>{footer}</div>}
      </motion.div>
    </>,
    document.body
  ) as ReactNode;
};

export { ActionSheet };
